<template>
  <div class="completion-container">
    <div v-if="loading" class="loading">
      Chargement des résultats...
    </div>

    <div v-else class="completion-content">
      <div class="completion-header">
        <div class="completion-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
        <h2>Test terminé !</h2>
      </div>

      <div class="completion-message">
        <p>Merci d'avoir complété le test <strong>{{ test.name }}</strong></p>
        <p class="completion-details" v-if="testResult && testResult.duration">
          Durée : {{ formatDuration(testResult.duration) }}
        </p>
      </div>

      <!-- CTA principal : tous les tests complétés -->
      <div v-if="allTestsCompleted && applicationId" class="all-completed-cta">
        <div class="cta-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
          </svg>
        </div>
        <h3>Tous vos tests sont complétés !</h3>
        <p>Découvrez la synthèse coaching de vos résultats.</p>
        <button @click="goToMyResults" class="results-button">
          Voir mes résultats
        </button>
      </div>

      <div class="next-steps">
        <h3>Prochaines étapes</h3>
        <div class="steps-list">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-text">
              Vos réponses ont été enregistrées avec succès
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-text">
              Les résultats seront analysés par notre équipe
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-text">
              Vous serez notifié de la suite du processus
            </div>
          </div>
        </div>
      </div>

      <div v-if="remainingTests.length > 0" class="remaining-tests">
        <h3>Tests restants</h3>
        <div class="tests-list">
          <div 
            v-for="test in remainingTests" 
            :key="test.id"
            class="test-item"
          >
            <span class="test-name">{{ test.name }}</span>
            <button 
              @click="startTest(test)"
              class="start-button"
            >
              Commencer
            </button>
          </div>
        </div>
      </div>

      <div class="navigation-buttons">
        <button 
          @click="goToTestsList"
          class="nav-button"
        >
          Retour à la liste des tests
        </button>
        
        <button 
          v-if="remainingTests.length === 0"
          @click="goToDashboard"
          class="nav-button primary"
        >
          Retour au tableau de bord
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TestCompleted',
  
  data() {
    return {
      loading: true,
      test: { name: '' },
      testResult: null,
      remainingTests: [],
      applicationId: null,
      allTestsCompleted: false
    }
  },

  async created() {
    await this.loadCompletionData();
  },

  methods: {
    async loadCompletionData() {
      try {
        const testId = this.$route.params.testId;
        // Récupérer les infos du test terminé pour déduire l'application_id
        try {
          const resp = await this.$http.get(`/tests/${testId}`);
          if (resp.data && resp.data.test) {
            this.test = { name: resp.data.test.name };
          }
        } catch (e) {
          // test complété => endpoint retourne une erreur, on utilise les query params
        }

        // L'application id est passée en query param si disponible
        const applicationId = this.$route.query.applicationId || this.$route.query.application_id;
        if (applicationId) {
          this.applicationId = applicationId;
          await this.checkAllTestsCompleted(applicationId);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données de fin de test:', error);
      } finally {
        this.loading = false;
      }
    },

    async checkAllTestsCompleted(applicationId) {
      try {
        const response = await this.$http.get(`/applications/${applicationId}/tests`);
        const assignedTests = response.data.assigned_tests || [];
        this.remainingTests = assignedTests
          .filter(t => t.status !== 'completed')
          .map(t => ({ id: t.id, name: t.test.name, type: t.test.type }));
        this.allTestsCompleted = assignedTests.length > 0
          && assignedTests.every(t => t.status === 'completed');
      } catch (e) {
        console.error('Erreur check all tests:', e);
      }
    },

    goToMyResults() {
      this.$router.push({
        name: 'MyTestResults',
        params: { applicationId: this.applicationId }
      });
    },

    formatDuration(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}min ${remainingSeconds}s`;
    },

    startTest(test) {
      this.$router.push({
        name: 'test-taking',
        params: { 
          testId: test.id,
          testType: test.type
        }
      });
    },

    goToTestsList() {
      this.$router.push({ name: 'tests-list' });
    },

    goToDashboard() {
      this.$router.push({ name: 'dashboard' });
    }
  }
}
</script>

<style scoped>
.completion-container {
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
}

.completion-content {
  background: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.completion-header {
  text-align: center;
  margin-bottom: 30px;
}

.completion-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  color: #4CAF50;
}

.completion-message {
  text-align: center;
  margin-bottom: 40px;
}

.completion-details {
  color: #666;
  margin-top: 10px;
}

.next-steps {
  margin-bottom: 40px;
}

.steps-list {
  margin-top: 20px;
}

.step-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.step-number {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #2596be;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-weight: bold;
}

.remaining-tests {
  margin-bottom: 40px;
}

.tests-list {
  margin-top: 20px;
}

.test-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 10px;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.nav-button {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.nav-button.primary {
  background-color: #2596be;
  color: white;
}

.start-button {
  background-color: #4CAF50;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.all-completed-cta {
  background: linear-gradient(135deg, #2596be 0%, #29b3cd 100%);
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  margin: 24px 0;
  color: white;
  box-shadow: 0 8px 20px rgba(37, 150, 190, 0.25);
}

.cta-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 16px;
  color: #ffd700;
}

.all-completed-cta h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.all-completed-cta p {
  margin-bottom: 20px;
  opacity: 0.95;
}

.results-button {
  background: white;
  color: #2596be;
  padding: 12px 28px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.results-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(0,0,0,0.15);
}
</style> 